import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { CancelTokenSource } from 'axios';
import Configs from 'config';
import { get, map } from 'lodash';
import { DirectCallOption } from 'sendbird-calls';
import { AuthOption } from 'sendbird-calls';
import apiClient, { WrappingApiClient } from 'libs/api/apiClient';
import { getUrlQuery } from 'libs/utils';
import { ERoute } from 'enums/route.enum';
import { Button, Divider, Modal } from 'antd';
import { LinkOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import menteeFileOff from 'assets/icons/ic-mentee-file-off.webp';
import menteeFileOn from 'assets/icons/ic-mentee-file-on.webp';
import menteeInfoOff from 'assets/icons/ic-mentee-info-off.webp';
import menteeInfoOn from 'assets/icons/ic-mentee-info-on.webp';
import videoOffIcon from 'assets/icons/ic_video_off_screen.webp';
import {
  MuteButton,
  UnmuteButton,
  StartVideoButton,
  StopVideoButton,
  EndButton,
} from '../../../components/atoms/CallButtons';
import { useSbCalls } from '../../../lib/sendbird-calls';
import * as mixins from '../../../styles/mixins';
import { media } from '../../../utils';
import Overlay from '../../atoms/Overlay';
import Screen from '../../templates/Screen/Screen';
import CallView from '../../views/CallView';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #000000;
  padding-bottom: 55px; // TabToolbar height
  ${media.main} {
    padding-bottom: 0;
  }
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const Contents = styled(Screen)`
  ${mixins.flexCenter};
  flex-direction: column;
  position: relative;
  width: 100%;
`;

const VideoOffView = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoOffIcon = styled.div<{ isConnected: Boolean }>`
  width: ${(props) => (props.isConnected ? '160px' : '30px')};
  height: ${(props) => (props.isConnected ? '160px' : '30px')};
  background-image: url(${videoOffIcon});
  background-size: ${(props) => (props.isConnected ? '160px' : '30px')};
  z-index: 9;
`;

interface DirectCallMainProps {}
const DirectCallMain: React.FC<DirectCallMainProps> = () => {
  const { calls } = useSbCalls();
  const sbCalls = useSbCalls();
  const location = useLocation();
  const history = useHistory();

  const preventGoBack = () => {
    window.history.pushState(null, '', window.location.href);
    history.push(`${ERoute.MentoringClassView.replace(':record_id', mentoring_id)}`);
    window.sessionStorage.removeItem('MenteeData');
  };

  const errorModal = () => {
    Modal.error({
      title: '잘못된 접근입니다.',
      onOk() {
        history.push(`${ERoute.MentoringClassView.replace(':record_id', mentoring_id)}`);
      },
    });
  };

  useEffect(() => {
    if (window.sessionStorage.getItem('MenteeData') === null) {
      errorModal();
    } else {
      login(my_id);
    }
  }, []);

  const { app_id, my_id, partner_id, mentoring_id } = getUrlQuery(location.search);

  const [showVideo, setShowVideo] = useState(false);
  const toggleShowVideo = (on: boolean) => {
    setShowVideo(on);
  };
  const [onMicrophone, setOnMicrophone] = useState(true);
  const toggleMicrophone = (on: boolean) => {
    setOnMicrophone(on);
  };
  const [onMenteeInfo, setOnMenteeInfo] = useState(false);
  const toggleMenteeInfo = (on: boolean) => {
    setOnMenteeFile(false);
    setOnMenteeInfo(on);
  };
  const [onMenteeFile, setOnMenteeFile] = useState(false);
  const toggleMenteeFile = (on: boolean) => {
    setOnMenteeInfo(false);
    setOnMenteeFile(on);
  };

  const onCall = useMemo(() => {
    return calls.find((call) => call.isOngoing);
  }, [calls]);

  function getCallOption(callOption?: DirectCallOption) {
    return {
      localMediaView: undefined,
      remoteMediaView: undefined,
      videoEnabled: showVideo,
      audioEnabled: onMicrophone,
      ...callOption,
    };
  }

  let userId = partner_id;
  const dial = (isVideoCall: boolean) => {
    return sbCalls.dial({ userId, isVideoCall, callOption: getCallOption({}) });
  };

  function postAPIWithDial() {
    const callInfo = dial(true);
    const params = new URLSearchParams(location.search);
    const mentoringId = params.get('mentoring_id');
    callInfo.then((res) => {
      apiCallPostCallId({ mentoring_id: Number(mentoringId), call_id: res.callId });
    });
  }

  type TSendbirdCallId = {
    mentoring_id: number;
    call_id: string;
  };

  const apiCallPostCallId = (params: TSendbirdCallId, cancel?: CancelTokenSource) => {
    return WrappingApiClient(
      apiClient.post(`/sendbird/call`, params, {
        cancelToken: cancel?.token,
      }),
    );
  };

  const [auth, setAuth] = useState(false);
  const login = (userId: string) => {
    const option: AuthOption = { userId };

    sbCalls.init(app_id);
    return sbCalls.auth(option).catch((error) => {
      console.log(error);
    });
  };

  useEffect(() => {
    if (window.sessionStorage.getItem('MenteeData') !== null) {
      const timer = () => setTimeout(() => setAuth(true), 2000);
      timer();
      if (auth) {
        if (onCall === undefined) {
          postAPIWithDial();
        }
      }
      window.clearTimeout(timer());
    }
  }, [auth]);

  const isMentorStorage =
    window.sessionStorage.getItem('MenteeData') !== undefined &&
    window.sessionStorage.getItem('MenteeData') !== 'none';

  return (
    <Wrapper
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      {window.sessionStorage.getItem('MenteeData') !== null && (
        <>
          <Contents>
            {onCall && (
              <Overlay onMenteeInfo={onMenteeInfo} onMenteeFile={onMenteeFile}>
                <CallView
                  call={onCall}
                  dial={postAPIWithDial}
                  showVideo={showVideo}
                  setShowVideo={toggleShowVideo}
                  onMicrophone={onMicrophone}
                  setOnMicrophone={toggleMicrophone}
                  onMenteeInfo={onMenteeInfo}
                  setOnMenteeInfo={toggleMenteeInfo}
                  onMenteeFile={onMenteeFile}
                  setOnMenteeFile={toggleMenteeFile}
                  backHandler={preventGoBack}
                />
              </Overlay>
            )}
            {!onCall && (
              <>
                {!showVideo && (
                  <VideoOffView>
                    <VideoOffIcon isConnected={true} />
                  </VideoOffView>
                )}
                <VideoFooter>
                  <TimerContainer />
                  <BtnContainer>
                    {onMicrophone ? (
                      <MuteButton key='mute-audio' onClick={() => setOnMicrophone(false)} />
                    ) : (
                      <UnmuteButton key='unmute-audio' onClick={() => setOnMicrophone(true)} />
                    )}
                    {showVideo ? (
                      <StartVideoButton key='stop-video' onClick={() => setShowVideo(false)} />
                    ) : (
                      <StopVideoButton key='start-video' onClick={() => setShowVideo(true)} />
                    )}
                    <EndButton
                      onClick={() => {
                        history.push(`${ERoute.MentoringClassView.replace(':record_id', mentoring_id)}`);
                        window.sessionStorage.removeItem('MenteeData');
                        window.location.reload();
                      }}
                    />
                  </BtnContainer>
                  {isMentorStorage ? (
                    <BtnContainer2>
                      {onMenteeInfo ? (
                        <IconBox>
                          <MenteeInfoOnIcon onClick={() => setOnMenteeInfo(false)} />
                          <div>요청사항</div>
                        </IconBox>
                      ) : (
                        <IconBox>
                          <MenteeInfoOffIcon
                            onClick={() => (setOnMenteeInfo(true), setOnMenteeFile(false))}
                          />
                          <div>요청사항</div>
                        </IconBox>
                      )}
                      {onMenteeFile ? (
                        <IconBox2>
                          <MenteeFileOnIcon onClick={() => setOnMenteeFile(false)} />
                          <div>첨부파일</div>
                        </IconBox2>
                      ) : (
                        <IconBox2>
                          <MenteeFileOffIcon
                            onClick={() => (setOnMenteeFile(true), setOnMenteeInfo(false))}
                          />
                          <div>첨부파일</div>
                        </IconBox2>
                      )}
                    </BtnContainer2>
                  ) : (
                    <BtnContainer />
                  )}
                </VideoFooter>
              </>
            )}
          </Contents>
          {onMenteeInfo && (
            <VideoModal>
              <ModalTitle>멘토링 세부정보</ModalTitle>
              <SubTitle>희망토픽</SubTitle>
              {map(
                get(JSON.parse(window.sessionStorage.getItem('MenteeData')!), 'desired_topics', []),
                (v) => (
                  <Button className='btn-topic' style={{ marginRight: '10px' }}>
                    {get(v, 'name')}
                  </Button>
                ),
              )}
              <Divider />
              <SubTitle>추가 요청사항</SubTitle>
              <div className='content-body'>
                {get(JSON.parse(window.sessionStorage.getItem('MenteeData')!), 'requirement')}
              </div>
            </VideoModal>
          )}
          {onMenteeFile && (
            <VideoModal>
              <ModalTitle>멘티가 첨부한 파일</ModalTitle>
              <SubTitle>파일 목록</SubTitle>
              {map(get(JSON.parse(window.sessionStorage.getItem('MenteeData')!), 'files', []), (v) => (
                <div style={{ margin: '5px 0px' }}>
                  <LinkOutlined />
                  <span style={{ fontSize: '16px', margin: '0px 10px' }}>{get(v, 'file_name')}</span>
                  <Button
                    type='primary'
                    className='download-btn'
                    href={`${Configs.API_HOST}${get(v, 'absolute_url')}`}
                  >
                    <DownloadOutlined />
                  </Button>
                </div>
              ))}
            </VideoModal>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default DirectCallMain;

const VideoModal = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 360px;
  min-height: calc(100% - 80px);
  height: calc(100% - 80px);
  border-radius: 20px;
  background-color: #ffffff;
  z-index: 10;
  padding: 20px;
  overflow-y: auto;
`;

const ModalTitle = styled.div`
  margin-bottom: 40px;
  font-size: 30px;
`;

const SubTitle = styled.div`
  font-size: 24px;
`;

const VideoFooter = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 70px;
  padding: 20px;
  background-color: #000000;
  position: absolute;
  bottom: 0;
`;

const TimerContainer = styled.div`
  flex: 1;
`;

const BtnContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BtnContainer2 = styled.div`
  flex: 1;
  display: flex;
  justify-content: end;
  align-items: center;
`;

const MenteeInfoOffIcon = styled.div`
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-color: transparent;
  border-radius: 50%;
  margin: 0 10px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 20px;
  background-image: url(${menteeInfoOn});
`;

const MenteeFileOffIcon = styled.div`
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-color: transparent;
  border-radius: 50%;
  margin: 0 10px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 20px;
  background-image: url(${menteeFileOn});
`;

const IconBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 12px;
  text-align: center;
  color: #ffffff;
`;

const IconBox2 = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 70px;
  justify-content: center;
  font-size: 12px;
  text-align: center;
  color: #ffffff;
`;

const Timer = () => {
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const countdown = setInterval(() => {
      setSeconds(seconds + 1);

      if (seconds === 59) {
        setMinutes(minutes + 1);
        setSeconds(0);
      }
    }, 1000);
    return () => clearInterval(countdown);
  }, [minutes, seconds]);

  return (
    <div>
      {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
    </div>
  );
};

const MenteeFileOnIcon = styled.div`
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-color: transparent;
  border-radius: 50%;
  margin: 0 10px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 20px;
  background-image: url(${menteeFileOff});
`;

const MenteeInfoOnIcon = styled.div`
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-color: transparent;
  border-radius: 50%;
  margin: 0 10px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 20px;
  background-image: url(${menteeInfoOff});
`;
